import { FormField, TextInput, ToggleSwitch } from "../../components/ui";
import { hojeIso, horaParaMinutos, minutosParaHora } from "./pontoUtils";

// Campos de horário (na ordem do cartão): entrada, saída, volta do intervalo, saída final.
const CAMPOS_SEMANA = [
  "semanaEntrada1",
  "semanaSaida1",
  "semanaEntrada2",
  "semanaSaida2",
];
const CAMPOS_SABADO = [
  "sabadoEntrada1",
  "sabadoSaida1",
  "sabadoEntrada2",
  "sabadoSaida2",
];
const ROTULOS = ["Entrada", "Saída", "Volta do intervalo", "Saída final"];

export const JORNADA_VAZIA = {
  participa: false,
  dataInicio: "",
  cargo: "",
  semanaEntrada1: "",
  semanaSaida1: "",
  semanaEntrada2: "",
  semanaSaida2: "",
  sabadoEntrada1: "",
  sabadoSaida1: "",
  sabadoEntrada2: "",
  sabadoSaida2: "",
  // Jornada legada (só carga por dia, sem horários): preservada ao editar, para não ser zerada.
  minutosSegunda: 0,
  minutosTerca: 0,
  minutosQuarta: 0,
  minutosQuinta: 0,
  minutosSexta: 0,
  minutosSabado: 0,
  minutosDomingo: 0,
};

/** Converte a jornada da API para o estado do formulário. */
export const jornadaParaFormulario = (jornada) => {
  if (!jornada) {
    return { ...JORNADA_VAZIA, dataInicio: hojeIso() };
  }
  const formulario = { ...JORNADA_VAZIA, participa: jornada.ativo };
  Object.keys(JORNADA_VAZIA).forEach((campo) => {
    if (campo !== "participa" && jornada[campo] != null) {
      formulario[campo] = jornada[campo];
    }
  });
  return formulario;
};

/** Monta o corpo do PUT /ponto/colaboradoras/{id}/jornada. */
export const formularioParaJornada = (formulario) => ({
  dataInicio: formulario.dataInicio,
  ativo: formulario.participa,
  cargo: formulario.cargo?.trim() || null,
  ...Object.fromEntries(
    [...CAMPOS_SEMANA, ...CAMPOS_SABADO].map((campo) => [
      campo,
      formulario[campo] || null,
    ]),
  ),
  // Com horário informado o servidor ignora estes valores; sem horário, vale a carga legada.
  minutosSegunda: formulario.minutosSegunda,
  minutosTerca: formulario.minutosTerca,
  minutosQuarta: formulario.minutosQuarta,
  minutosQuinta: formulario.minutosQuinta,
  minutosSexta: formulario.minutosSexta,
  minutosSabado: formulario.minutosSabado,
  minutosDomingo: formulario.minutosDomingo,
});

const cargaDoDia = (formulario, campos) => {
  const [e1, s1, e2, s2] = campos.map((campo) =>
    horaParaMinutos(formulario[campo]),
  );
  if (
    !formulario[campos[0]] ||
    !formulario[campos[1]] ||
    e1 === null ||
    s1 === null
  )
    return 0;
  let total = Math.max(0, s1 - e1);
  if (
    formulario[campos[2]] &&
    formulario[campos[3]] &&
    e2 !== null &&
    s2 !== null
  ) {
    total += Math.max(0, s2 - e2);
  }
  return total;
};

const LinhaHorario = ({
  titulo,
  campos,
  formulario,
  onChange,
  desabilitado,
  prefixoId,
}) => {
  const carga = cargaDoDia(formulario, campos);
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">{titulo}</p>
        <p className="text-xs text-gray-500">
          {carga ? `Carga do dia: ${minutosParaHora(carga)}` : "Sem expediente"}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {campos.map((campo, indice) => (
          <FormField
            key={campo}
            label={ROTULOS[indice]}
            htmlFor={`${prefixoId}-${campo}`}
          >
            <TextInput
              id={`${prefixoId}-${campo}`}
              type="time"
              value={formulario[campo]}
              disabled={desabilitado}
              onChange={(evento) =>
                onChange({ ...formulario, [campo]: evento.target.value })
              }
            />
          </FormField>
        ))}
      </div>
    </div>
  );
};

/**
 * Cargo e horário de trabalho da colaboradora. A carga de cada dia do ponto é calculada a partir
 * daqui (segunda a sexta pelo horário da semana, sábado pelo horário de sábado, domingo sem expediente).
 */
const HorarioTrabalhoCampos = ({
  formulario,
  onChange,
  prefixoId = "jornada",
  mostrarParticipa = true,
}) => {
  const desabilitado = mostrarParticipa && !formulario.participa;
  return (
    <div className="space-y-4">
      {mostrarParticipa && (
        <ToggleSwitch
          checked={formulario.participa}
          onChange={(evento) =>
            onChange({ ...formulario, participa: evento.target.checked })
          }
          label="Controla ponto (bate ponto e aparece no cartão ponto)"
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Cargo"
          htmlFor={`${prefixoId}-cargo`}
          hint="Sai no cartão ponto."
        >
          <TextInput
            id={`${prefixoId}-cargo`}
            maxLength={120}
            placeholder="Ex.: Recepcionista, Auxiliar de saúde bucal"
            value={formulario.cargo}
            disabled={desabilitado}
            onChange={(evento) =>
              onChange({ ...formulario, cargo: evento.target.value })
            }
          />
        </FormField>
        <FormField
          label="Início do controle de ponto"
          htmlFor={`${prefixoId}-inicio`}
          hint="Dias anteriores não geram falta."
        >
          <TextInput
            id={`${prefixoId}-inicio`}
            type="date"
            value={formulario.dataInicio}
            disabled={desabilitado}
            onChange={(evento) =>
              onChange({ ...formulario, dataInicio: evento.target.value })
            }
          />
        </FormField>
      </div>

      <LinhaHorario
        titulo="Horário de segunda a sexta"
        campos={CAMPOS_SEMANA}
        formulario={formulario}
        onChange={onChange}
        desabilitado={desabilitado}
        prefixoId={prefixoId}
      />
      <LinhaHorario
        titulo="Horário de sábado (deixe em branco se não trabalha)"
        campos={CAMPOS_SABADO}
        formulario={formulario}
        onChange={onChange}
        desabilitado={desabilitado}
        prefixoId={prefixoId}
      />
      <p className="text-xs text-gray-500">
        Sem intervalo no dia? Preencha só Entrada e Saída. Domingo fica sem
        expediente.
      </p>
    </div>
  );
};

export default HorarioTrabalhoCampos;
